import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import {
  CALL_REROUTED,
  RTI_DOWNSTREAM_SHARE,
} from '@rsos/constants/collaborator';
import { collaboratorMessageTypeSchema } from '@rsos/reduxDataStore/src/features/emergencyCalls/collaboratorSchemas';
import {
  addCollaborator,
  addLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';

// StreamWS fans every websocket message through this callback, so only the
// envelope shape is validated here -- `type` narrows down to collaborator
// events (replacing the old Set-membership check), and `body` is otherwise
// left as an untyped record. The collaborator body's own fields are
// validated once, in buildCollaboratorPayload, rather than duplicated here.
const streamMessageEnvelopeSchema = z.object({
  type: z.string(),
  body: z.record(z.string(), z.unknown()).optional(),
});

const useHandleCollaboratorMessage = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch = useDispatch();

  const handleCollaboratorMessage = useCallback(
    (msg: Record<string, unknown>) => {
      const parsedEnvelope = streamMessageEnvelopeSchema.safeParse(msg);
      if (!parsedEnvelope.success) return;

      const { type, body = {} } = parsedEnvelope.data;

      const parsedType = collaboratorMessageTypeSchema.safeParse(type);
      if (!parsedType.success) return;

      const messageType = parsedType.data;
      const payload = { ...body, messageType };

      // Add collaborator metadata
      dispatch(addCollaborator(payload));

      // For CALL_REROUTED and RTI_DOWNSTREAM_SHARE, also add location data
      // to plot marker and draw line
      if (
        messageType === CALL_REROUTED ||
        messageType === RTI_DOWNSTREAM_SHARE
      ) {
        const extraInfo = (body.extra_info as Record<string, unknown>) || {};
        const locationPayload = {
          ...body,
          // Ensure application starts with 'ali_' for proper secondary location handling
          application: body.application || 'ali_collaborator_rtt_ingress',
          // class_of_service_category should start with 'Phase' for wireless secondary locations
          class_of_service_category: body.class_of_service_category || 'Phase1',
          extra_info: {
            ...extraInfo,
            payload_id: extraInfo.payload_id || '',
            request_id: extraInfo.request_id || '',
          },
        };
        dispatch(addLocation(locationPayload));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleCollaboratorMessage: handleCollaboratorMessage,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleCollaboratorMessage');
      };
    }
  }, [StreamWS, handleCollaboratorMessage]);
};

export default useHandleCollaboratorMessage;
